import { Odts, OdtsV2 } from "@features/odts/models/Odts";
import { create, StateCreator } from "zustand";
import { devtools } from "zustand/middleware";

export type OdtState = {
  odts: (Odts | OdtsV2)[];
  loading: boolean;
  setOdts: (response: (Odts | OdtsV2)[]) => void;
  setLoadingOdt: (loading: boolean) => void;
  removeOdt: (odtToRemove: Odts | OdtsV2) => void;
  addOdt: (odtToAdd: Odts | OdtsV2) => void;
  updateOdt: (odtToUpdate: Odts | OdtsV2) => void;
  getOdt: (folio: string) => Odts | OdtsV2 | null;
  reset: () => void;
  // Filters persistence
  odtFilters: Record<string, any>;
  setOdtsFilters: (query: string, filters: any) => void;
  getOdtsFilters: (query: string) => any | null;
  resetOdtsFilters: (query: string) => void;
  // Active tab persistence
  odtActiveTabs: Record<string, string>;
  setOdtActiveTab: (query: string, tab: string) => void;
  getOdtActiveTab: (query: string) => string | null;
  resetOdtActiveTab: (query: string) => void;
};

const storeApi: StateCreator<OdtState> = (set, get) => ({
  odts: [],
  loading: false,
  setOdts: (response: (Odts | OdtsV2)[]) => {
    set({ odts: response, loading: false });
  },
  setLoadingOdt: (loading: boolean) => {
    set({ loading });
  },
  removeOdt: (odtToRemove: Odts | OdtsV2) => {
    set((state) => ({
      odts: state.odts.filter((odt) => odt.folio !== odtToRemove.folio),
    }));
  },
  addOdt: (odtToAdd: Odts | OdtsV2) => {
    set((state) => ({
      odts: [...state.odts, odtToAdd],
    }));
  },
  updateOdt: (odtToUpdate: Odts | OdtsV2) => {
    set((state) => ({
      odts: state.odts.map((odt) => 
        odt.folio === odtToUpdate.folio ? odtToUpdate : odt
      ),
    }));
  },
  reset: () => {
    set({ odts: [], loading: false });
  },
  getOdt: (folio: string) => {
    const { odts } = get();
    return odts.find((odt) => odt.folio === folio) || null;
  },
  odtFilters: {},
  odtActiveTabs: {},
  setOdtsFilters: (query: string, filters: any) => {
    set((state) => ({ odtFilters: { ...state.odtFilters, [query]: filters } }));
    try {
      localStorage.setItem(`odtsFilters_${query}`, JSON.stringify(filters));
    } catch (e) {
      console.log("Error saving odts filters to localStorage", e);
    }
  },
  setOdtActiveTab: (query: string, tab: string) => {
    set((state) => ({ odtActiveTabs: { ...state.odtActiveTabs, [query]: tab } }));
    try {
      localStorage.setItem(`odtsActiveTab_${query}`, tab);
    } catch (e) {
      console.log("Error saving odt active tab to localStorage", e);
    }
  },
  getOdtsFilters: (query: string) => {
    const stateFilters = get().odtFilters[query];
    if (stateFilters) return stateFilters;
    try {
      const raw = localStorage.getItem(`odtsFilters_${query}`);
      if (raw) return JSON.parse(raw);
    } catch (e) {
      console.log("Error reading odts filters from localStorage", e);
    }
    return null;
  },
  getOdtActiveTab: (query: string) => {
    const stateTab = get().odtActiveTabs[query];
    if (stateTab) return stateTab;
    try {
      const raw = localStorage.getItem(`odtsActiveTab_${query}`);
      if (raw) return raw;
    } catch (e) {
      console.log("Error reading odt active tab from localStorage", e);
    }
    return null;
  },
  resetOdtsFilters: (query: string) => {
    set((state) => {
      const copy = { ...state.odtFilters };
      delete copy[query];
      return { odtFilters: copy };
    });
    try {
      localStorage.removeItem(`odtsFilters_${query}`);
    } catch (e) {
      console.log("Error removing odts filters from localStorage", e);
    }
  },
  resetOdtActiveTab: (query: string) => {
    set((state) => {
      const copy = { ...state.odtActiveTabs };
      delete copy[query];
      return { odtActiveTabs: copy };
    });
    try {
      localStorage.removeItem(`odtsActiveTab_${query}`);
    } catch (e) {
      console.log("Error removing odt active tab from localStorage", e);
    }
  },
});

export const useOdtStore = create<OdtState>()(
  devtools(storeApi, { name: "odts" })
);
